import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDemo } from "@/context/DemoContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, AlertTriangle, Mail, FileSpreadsheet } from "lucide-react";
import { api } from "@/lib/api";
import { faseClass, statoClass, fmtDate, FASI_NOMI, isReadyForFatturazione } from "@/lib/helpers";
import { toast } from "sonner";
import FeedbackButton from "@/components/FeedbackButton";
import * as XLSX from "xlsx";

const EMAIL_SIM = {
  from: "manutenzione@example.com",
  subject: "Fermo linea ondulatrice - richiesta assistenza urgente",
  body: "Buongiorno, questa mattina la ondulatrice si è fermata con un allarme sul PLC. Vi chiediamo intervento urgente.\n\nCordiali saluti,\nUfficio Manutenzione",
};

function KpiCard({ label, value, onClick, tone="default", icon }) {
  const tones = { default: "text-slate-900", danger: "text-red-700", warning: "text-amber-700", info: "text-blue-800" };
  const accents = { default: "bg-slate-100", danger: "bg-red-100", warning: "bg-amber-100", info: "bg-blue-100" };
  return (
    <button onClick={onClick} className="card-clean p-4 text-left hover:shadow-md transition-shadow flex items-center gap-3" data-testid={`kpi-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className={`w-10 h-10 rounded-lg ${accents[tone]} flex items-center justify-center text-lg`}>{icon}</div>
      <div>
        <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">{label}</div>
        <div className={`text-2xl font-bold ${tones[tone]}`}>{value}</div>
      </div>
    </button>
  );
}

export default function DashboardPage() {
  const { data, reload, isTecnico, myTecnicoId, canEdit, user } = useDemo();
  const profile = { nome: user?.nome || "", perm: user?.role || "" };
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [filters, setFilters] = useState({
    sede: "all", stato: "all", divisione: "all", tecnico: "all", urgente: "all", tipoGestione: "all",
    cliente: "all", stabilimento: "all", tipoRichiesta: "all", classifHwsw: "all",
    responsabile: "", consuntivo: "all", fatturato: "all",
    dataAperturaFrom: "", dataAperturaTo: "",
    scadenzaFrom: "", scadenzaTo: "",
    soloOrfani: false,
  });
  const [openNew, setOpenNew] = useState(false);
  const [openEmail, setOpenEmail] = useState(false);
  const [reassignTicket, setReassignTicket] = useState(null); // ticket in fase di riassegnazione anagrafica
  const [reassignForm, setReassignForm] = useState({ cliente_id: "", stabilimento_id: "", contatto_id: "" });
  const [reassignBusy, setReassignBusy] = useState(false);
  // Match automatico orfani
  const [openMatch, setOpenMatch] = useState(false);
  const [matchSoglia, setMatchSoglia] = useState(0.5);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchApplying, setMatchApplying] = useState(false);
  const [matchResults, setMatchResults] = useState([]);       // [{ticket_id, numero, cliente_nome_storico, stabilimento_nome_storico, candidati:[...]}]
  const [matchSelection, setMatchSelection] = useState({});   // { ticket_id: { cliente_id, stabilimento_id, enabled } }

  // Sync search from URL param when it changes (from global search bar)
  useEffect(() => {
    const urlQ = searchParams.get("q") || "";
    if (urlQ !== q) setQ(urlQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Count stabilimenti per cliente for badge
  const stabPerCliente = useMemo(() => {
    const map = {};
    data.stabilimenti.forEach(s => { map[s.cliente_id] = (map[s.cliente_id] || 0) + 1; });
    return map;
  }, [data.stabilimenti]);
  const [form, setForm] = useState({
    numero: "",
    cliente_id: "", stabilimento_id: "", contatto_nome: "", contatto_email: "", contatto_telefono: "",
    oggetto: "", canale: "email", natura: "SW", urgente: false, sede: "Perugia", note_interne: "",
    data_richiesta: new Date().toISOString().slice(0, 10),
  });

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null); // "stato" | "chiudi" | "delete" | null
  const [bulkStatoValue, setBulkStatoValue] = useState("OnGoing");
  const [bulkMotivazione, setBulkMotivazione] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const interventiByTicket = useMemo(() => {
    // For tecnico filter, we need interventi list; but we already have that inside tickets? no
    // For now filter by name check
    return {};
  }, []);

  const filtered = useMemo(() => {
    let ts = data.tickets;
    const query = q.toLowerCase();
    // If query matches a tecnico (by name or email), also include tickets where responsabile matches that tecnico's name
    const tecniciMatch = query && (data.tecnici || []).filter(tc =>
      (tc.nome || "").toLowerCase().includes(query) ||
      (tc.email || "").toLowerCase().includes(query)
    );
    return ts.filter(t => {
      if (query) {
        const cli = data.clienti.find(c => c.id === t.cliente_id);
        const stab = data.stabilimenti.find(s => s.id === t.stabilimento_id);
        const hay = `${t.numero} ${t.codice_eg||""} ${t.commessa||""} ${cli?.ragione_sociale||""} ${t.cliente_nome_storico||""} ${stab?.nome||""} ${t.stabilimento_nome_storico||""} ${t.descrizione} ${t.responsabile||""} ${t.oggetto||""}`.toLowerCase();
        const tecnicoHit = tecniciMatch && tecniciMatch.some(tc => (t.responsabile||"").toLowerCase().includes((tc.nome||"").toLowerCase()));
        if (!hay.includes(query) && !tecnicoHit) return false;
      }
      if (filters.sede !== "all" && t.sede !== filters.sede) return false;
      if (filters.divisione !== "all" && !(t.divisioni_coinvolte || []).includes(filters.divisione)) return false;
      if (filters.tipoGestione !== "all" && (t.classificazione || "Da definire") !== filters.tipoGestione) return false;
      if (filters.cliente !== "all" && t.cliente_id !== filters.cliente) return false;
      if (filters.stabilimento !== "all" && t.stabilimento_id !== filters.stabilimento) return false;
      if (filters.tipoRichiesta !== "all" && (t.tipo_richiesta || "Intervento") !== filters.tipoRichiesta) return false;
      if (filters.classifHwsw !== "all" && (t.classificazione_hwsw || "SW") !== filters.classifHwsw) return false;
      if (filters.responsabile && !(t.responsabile||"").toLowerCase().includes(filters.responsabile.toLowerCase())) return false;
      if (filters.consuntivo === "yes" && !t.consuntivo_effettuato) return false;
      if (filters.consuntivo === "no" && t.consuntivo_effettuato) return false;
      if (filters.fatturato === "yes" && !(t.importo_fatturato_manuale > 0 || t.stato === "Chiuso")) return false;
      if (filters.fatturato === "no" && (t.importo_fatturato_manuale > 0)) return false;
      // Data apertura range (compare on data_richiesta / created_at YYYY-MM-DD)
      const dataApTs = (t.data_richiesta || t.created_at || "").slice(0,10);
      if (filters.dataAperturaFrom && dataApTs && dataApTs < filters.dataAperturaFrom) return false;
      if (filters.dataAperturaTo && dataApTs && dataApTs > filters.dataAperturaTo) return false;
      const scadTs = (t.data_prossima_azione || t.scadenza || "").slice(0,10);
      if (filters.scadenzaFrom && scadTs && scadTs < filters.scadenzaFrom) return false;
      if (filters.scadenzaTo && scadTs && scadTs > filters.scadenzaTo) return false;
      if (filters.stato !== "all") {
        if (filters.stato === "attesa_cliente" && t.stato !== "In attesa cliente") return false;
        if (filters.stato === "aperto" && (t.stato === "Chiuso" || t.stato === "Annullato")) return false;
        if (filters.stato === "chiuso" && t.stato !== "Chiuso") return false;
        if (filters.stato === "urgente" && !t.urgente) return false;
        if (filters.stato === "fatturabile" && !isReadyForFatturazione(t)) return false;
        if (filters.stato === "ongoing" && t.stato !== "OnGoing") return false;
        if (filters.stato === "sospesa" && t.stato !== "Sospeso" && t.stato !== "Sospesa") return false;
        if (filters.stato === "annullata" && t.stato !== "Annullato" && t.stato !== "Annullata") return false;
      }
      if (filters.urgente === "yes" && !t.urgente) return false;
      if (filters.soloOrfani && t.cliente_id) return false;
      return true;
    }).sort((a,b) => (b.numero||"").localeCompare(a.numero||""));
  }, [data, q, filters, isTecnico, myTecnicoId]);

  const kpi = useMemo(() => ({
    aperti: data.tickets.filter(t => t.stato !== "Chiuso" && t.stato !== "Annullato").length,
    urgenti: data.tickets.filter(t => t.urgente && t.stato !== "Chiuso").length,
    attesa: data.tickets.filter(t => t.stato === "In attesa cliente").length,
    fatturabili: data.tickets.filter(isReadyForFatturazione).length,
    orfani: data.tickets.filter(t => !t.cliente_id).length,
  }), [data]);

  const reassignStabOptions = useMemo(() => (
    data.stabilimenti.filter(s => s.cliente_id === reassignForm.cliente_id).map(s => ({ value: s.id, label: s.nome }))
  ), [data.stabilimenti, reassignForm.cliente_id]);
  const reassignContattoOptions = useMemo(() => (
    data.contatti
      .filter(c => c.cliente_id === reassignForm.cliente_id || c.stabilimento_id === reassignForm.stabilimento_id)
      .map(c => ({ value: c.id, label: c.nome }))
  ), [data.contatti, reassignForm.cliente_id, reassignForm.stabilimento_id]);

  const openReassign = (t) => {
    setReassignTicket(t);
    setReassignForm({ cliente_id: "", stabilimento_id: "", contatto_id: "" });
  };
  const saveReassign = async () => {
    if (!reassignTicket) return;
    if (!reassignForm.cliente_id || !reassignForm.stabilimento_id) {
      toast.error("Seleziona cliente e stabilimento");
      return;
    }
    setReassignBusy(true);
    try {
      await api.put(`/tickets/${reassignTicket.id}`, {
        cliente_id: reassignForm.cliente_id,
        stabilimento_id: reassignForm.stabilimento_id,
        contatto_id: reassignForm.contatto_id || null,
        cliente_nome_storico: "",
        stabilimento_nome_storico: "",
      });
      toast.success(`Ticket ${reassignTicket.numero} riassegnato all'anagrafica`);
      setReassignTicket(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore in riassegnazione");
    } finally {
      setReassignBusy(false);
    }
  };

  const loadMatchSuggestions = async (soglia) => {
    setMatchLoading(true);
    try {
      const s = typeof soglia === "number" ? soglia : matchSoglia;
      const r = await api.post("/tickets/orfani/match_suggerimenti", { soglia: s, top_n: 3 });
      setMatchResults(r.risultati || []);
      // Preseleziona il best candidate di ognuno
      const sel = {};
      (r.risultati || []).forEach(row => {
        const best = row.candidati?.[0];
        if (best) {
          sel[row.ticket_id] = {
            cliente_id: best.cliente_id,
            stabilimento_id: best.best_stabilimento?.id || "",
            enabled: ((best.score_combinato ?? best.score_cliente ?? 0) >= 0.7), // preseleziona solo alta confidenza (combinato)
          };
        } else {
          sel[row.ticket_id] = { cliente_id: "", stabilimento_id: "", enabled: false };
        }
      });
      setMatchSelection(sel);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel calcolo dei match");
    } finally {
      setMatchLoading(false);
    }
  };
  const openMatchDialog = async () => {
    setOpenMatch(true);
    await loadMatchSuggestions(matchSoglia);
  };
  const applyMatches = async () => {
    const apps = Object.entries(matchSelection)
      .filter(([, v]) => v.enabled && v.cliente_id)
      .map(([ticket_id, v]) => ({ ticket_id, cliente_id: v.cliente_id, stabilimento_id: v.stabilimento_id || null }));
    if (!apps.length) { toast.error("Nessun match selezionato"); return; }
    setMatchApplying(true);
    try {
      const r = await api.post("/tickets/orfani/apply_matches", { applications: apps });
      toast.success(`${r.aggiornati} ticket riassegnati${r.errori?.length ? ` (${r.errori.length} errori)` : ""}`);
      setOpenMatch(false);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'applicazione dei match");
    } finally {
      setMatchApplying(false);
    }
  };

  // Bulk selection helpers
  const toggleSel = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelectedIds(new Set());
  const isAllFilteredSel = filtered.length > 0 && filtered.every(t => selectedIds.has(t.id));
  const toggleAllFiltered = () => setSelectedIds(prev => {
    if (filtered.every(t => prev.has(t.id))) {
      const n = new Set(prev); filtered.forEach(t => n.delete(t.id)); return n;
    }
    const n = new Set(prev); filtered.forEach(t => n.add(t.id)); return n;
  });
  const runBulk = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      if (bulkAction === "stato") {
        const r = await api.post("/tickets/bulk_stato", { ids, stato: bulkStatoValue });
        toast.success(`${r.aggiornati} ticket aggiornati a "${bulkStatoValue}"`);
      } else if (bulkAction === "chiudi") {
        const r = await api.post("/tickets/bulk_chiudi", { ids, motivazione: bulkMotivazione || "Chiusura in blocco" });
        toast.success(`${r.aggiornati} ticket chiusi`);
      } else if (bulkAction === "delete") {
        const r = await api.post("/tickets/bulk_delete", { ids });
        toast.success(`${r.eliminati} ticket eliminati`);
      }
      setBulkAction(null); setBulkMotivazione(""); clearSel();
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'operazione in blocco");
    } finally {
      setBulkBusy(false);
    }
  };

  const stabilimentiFiltrati = data.stabilimenti.filter(s => s.cliente_id === form.cliente_id);

  // Validazione live numero duplicato (case-insensitive)
  const numeroDuplicato = useMemo(() => {
    const n = (form.numero || "").trim().toLowerCase();
    if (!n) return null;
    const match = (data.tickets || []).find(t => (t.numero || "").trim().toLowerCase() === n);
    return match ? match.numero : null;
  }, [form.numero, data.tickets]);

  const createTicket = async () => {
    if (!form.numero.trim()) {
      toast.error("Inserisci il numero ticket"); return;
    }
    if (numeroDuplicato) {
      toast.error(`Numero "${numeroDuplicato}" già esistente. Scegli un numero diverso.`); return;
    }
    if (!form.cliente_id || !form.stabilimento_id || !form.oggetto.trim()) {
      toast.error("Cliente, stabilimento e oggetto sintetico sono obbligatori"); return;
    }
    try {
      const dataIso = form.data_richiesta ? new Date(form.data_richiesta + "T00:00:00").toISOString() : new Date().toISOString();
      const t = await api.post("/tickets", { ...form, numero: form.numero.trim(), data_richiesta: dataIso });
      toast.success(`Ticket ${t.numero} creato · completa la descrizione per chiudere la Fase 1`);
      setOpenNew(false); resetForm();
      await reload();
      nav(`/ticket/${t.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409) {
        toast.error(detail || "Numero ticket già esistente");
      } else {
        toast.error(detail || "Errore nella creazione del ticket");
      }
    }
  };

  const createBozzaFromEmail = async () => {
    // Crea bozza pre-compilata dall'email fittizia
    const t = await api.post("/tickets", {
      cliente_id: "", stabilimento_id: "", contatto_nome: "",
      descrizione: `[Da email simulata da ${EMAIL_SIM.from}] ${EMAIL_SIM.subject}\n\n${EMAIL_SIM.body}`,
      canale: "email", natura: "SW", urgente: true, sede: "Perugia",
      note_interne: `Email simulata - non collegata a caselle reali. Message-Id fittizio: ${Date.now()}`,
    });
    toast.success("Bozza creata da email simulata. Completa cliente e stabilimento.");
    setOpenEmail(false);
    await reload();
    nav(`/ticket/${t.id}`);
  };

  const resetForm = () => setForm({ numero: "", cliente_id: "", stabilimento_id: "", contatto_nome: "", contatto_email: "", contatto_telefono: "",
    oggetto: "", canale: "email", natura: "SW", urgente: false, sede: "Perugia", note_interne: "",
    data_richiesta: new Date().toISOString().slice(0, 10) });

  const exportRegistroXlsx = async (scope) => {
    try {
      const body = scope === "filtered" ? { scope: "filtered", ids: filtered.map(t => t.id) } : { scope: "all", ids: [] };
      if (scope === "filtered" && body.ids.length === 0) { toast.error("Nessun ticket filtrato da esportare"); return; }
      const API_BASE = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_BASE}/api/tickets/export-xlsx`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || `HTTP ${res.status}`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `Emmepi_Service_Registro_${today}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(scope === "all" ? "Registro completo esportato" : `Registro esportato (${body.ids.length} ticket filtrati)`);
    } catch (e) {
      toast.error(e.message || "Errore export");
    }
  };

  const exportExcel = () => {
    if (!filtered.length) { toast.error("Nessun ticket da esportare"); return; }
    const rows = filtered.map(t => {
      const cli = data.clienti.find(c => c.id === t.cliente_id);
      const stab = data.stabilimenti.find(s => s.id === t.stabilimento_id);
      const faseStato = (num) => {
        const f = (t.fasi || []).find(x => x.num === num);
        return f?.stato === "Completata" ? "Completata" : "";
      };
      const created = t.created_at ? new Date(t.created_at) : null;
      return {
        "ID Ticket": t.numero || "",
        "Cliente": cli?.ragione_sociale || "",
        "Stabilimento": stab?.nome || "",
        "Urgente": t.urgente ? "Sì" : "No",
        "Stato": t.stato || "",
        "Data creazione": created ? created.toLocaleString("it-IT") : "",
        [FASI_NOMI[0]]: faseStato(1),
        [FASI_NOMI[1]]: faseStato(2),
        [FASI_NOMI[2]]: faseStato(3),
        [FASI_NOMI[3]]: faseStato(4),
        [FASI_NOMI[4]]: faseStato(5),
        [FASI_NOMI[5]]: faseStato(6),
        [FASI_NOMI[6]]: faseStato(7),
        [FASI_NOMI[7]]: faseStato(8),
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    // Auto-width columns based on max content length
    const cols = Object.keys(rows[0] || {});
    ws["!cols"] = cols.map(k => ({
      wch: Math.min(40, Math.max(k.length + 2, ...rows.map(r => String(r[k] ?? "").length + 2))),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ticket");
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `emmepi-service-tickets-${stamp}.xlsx`);
    toast.success(`Esportati ${rows.length} ticket in Excel`);
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-sm text-slate-500">{filtered.length} ticket · profilo: <span className="font-medium text-slate-700">{profile.nome}</span></div>
        <div className="flex gap-2 flex-wrap">
          <FeedbackButton schermata="Cruscotto ticket" />
          {canEdit("ticket") && (
            <>
              <Button variant="outline" onClick={() => exportRegistroXlsx("all")} data-testid="btn-export-registro-all" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800">
                <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Esporta registro (tutto)
              </Button>
              <Button variant="outline" onClick={() => exportRegistroXlsx("filtered")} data-testid="btn-export-registro-filtered" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800">
                <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Esporta filtrati
              </Button>
            </>
          )}
          {canEdit("ticket") && (
            <>
              <Button variant="outline" onClick={() => setOpenEmail(true)} data-testid="btn-simula-email">
                <Mail className="w-4 h-4 mr-1.5" /> Simula richiesta email
              </Button>
              <Button onClick={() => setOpenNew(true)} data-testid="btn-nuovo-ticket" className="bg-blue-700 hover:bg-blue-800">
                <Plus className="w-4 h-4 mr-1.5" /> Nuovo ticket
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiCard label="OnGoing" value={kpi.aperti} tone="info" icon="📋" onClick={() => setFilters({ ...filters, stato: "ongoing" })} />
        <KpiCard label="Urgenti" value={kpi.urgenti} tone="danger" icon="🔥" onClick={() => setFilters({ ...filters, stato: "urgente" })} />
        <KpiCard label="Attesa cliente" value={kpi.attesa} tone="warning" icon="⏳" onClick={() => setFilters({ ...filters, stato: "attesa_cliente" })} />
        <KpiCard label="Pronti fatturaz." value={kpi.fatturabili} tone="info" icon="💰" onClick={() => setFilters({ ...filters, stato: "fatturabile" })} />
      </div>

      <Card className="card-clean p-3 mb-3">
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
            <Input placeholder="Cerca numero, E/G, commessa, cliente, tecnico, email…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="search-input" />
          </div>
          <Select value={filters.sede} onValueChange={v => setFilters({ ...filters, sede: v })}>
            <SelectTrigger className="w-[140px]" data-testid="filter-sede"><SelectValue placeholder="Sede" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le sedi</SelectItem>
              <SelectItem value="Perugia">Perugia</SelectItem>
              <SelectItem value="Treviso">Treviso</SelectItem>
              <SelectItem value="Milano">Milano</SelectItem>
              <SelectItem value="Novara">Novara</SelectItem>
              <SelectItem value="Pieri">Pieri</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.divisione} onValueChange={v => setFilters({ ...filters, divisione: v })}>
            <SelectTrigger className="w-[160px]" data-testid="filter-divisione"><SelectValue placeholder="Divisione" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le divisioni</SelectItem>
              {(data.divisioni || []).map(d => <SelectItem key={d.codice} value={d.codice}>{d.codice} · {d.descrizione}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.stato} onValueChange={v => setFilters({ ...filters, stato: v })}>
            <SelectTrigger className="w-[170px]" data-testid="filter-stato"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="ongoing">OnGoing (aperti)</SelectItem>
              <SelectItem value="sospesa">Sospesa</SelectItem>
              <SelectItem value="urgente">Urgenti</SelectItem>
              <SelectItem value="attesa_cliente">Attesa cliente</SelectItem>
              <SelectItem value="fatturabile">Pronti fatturaz.</SelectItem>
              <SelectItem value="chiuso">Chiusi</SelectItem>
              <SelectItem value="annullata">Annullati</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.tipoGestione} onValueChange={v => setFilters({ ...filters, tipoGestione: v })}>
            <SelectTrigger className="w-[170px]" data-testid="filter-tipo-gestione"><SelectValue placeholder="Tipo gestione" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti (E/G/Da def.)</SelectItem>
              <SelectItem value="Economia">Economia</SelectItem>
              <SelectItem value="Garanzia">Garanzia</SelectItem>
              <SelectItem value="Da definire">Da definire</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Second row of advanced filters */}
        <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
          <Select value={filters.cliente} onValueChange={v => setFilters({ ...filters, cliente: v, stabilimento: "all" })}>
            <SelectTrigger className="w-[200px]" data-testid="filter-cliente"><SelectValue placeholder="Cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {data.clienti.filter(c=>c.attivo).map(c => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.stabilimento} onValueChange={v => setFilters({ ...filters, stabilimento: v })}>
            <SelectTrigger className="w-[180px]" data-testid="filter-stabilimento"><SelectValue placeholder="Stabilimento" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stab.</SelectItem>
              {data.stabilimenti.filter(s => filters.cliente === "all" || s.cliente_id === filters.cliente).map(s => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.tipoRichiesta} onValueChange={v => setFilters({ ...filters, tipoRichiesta: v })}>
            <SelectTrigger className="w-[160px]" data-testid="filter-tipo-richiesta"><SelectValue placeholder="Tipo richiesta" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              <SelectItem value="Intervento">Intervento</SelectItem>
              <SelectItem value="Modifica">Modifica</SelectItem>
              <SelectItem value="ANALISI">ANALISI</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.classifHwsw} onValueChange={v => setFilters({ ...filters, classifHwsw: v })}>
            <SelectTrigger className="w-[140px]" data-testid="filter-classif-hwsw"><SelectValue placeholder="HW/SW" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="HW">HW</SelectItem>
              <SelectItem value="SW">SW</SelectItem>
              <SelectItem value="HW+SW">HW+SW</SelectItem>
              <SelectItem value="DOC">DOC</SelectItem>
              <SelectItem value="Altro">Altro</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Responsabile…"
            value={filters.responsabile}
            onChange={e => setFilters({ ...filters, responsabile: e.target.value })}
            className="w-[160px]"
            data-testid="filter-responsabile"
          />
          <Select value={filters.consuntivo} onValueChange={v => setFilters({ ...filters, consuntivo: v })}>
            <SelectTrigger className="w-[160px]" data-testid="filter-consuntivo"><SelectValue placeholder="Consuntivo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti (consuntivo)</SelectItem>
              <SelectItem value="yes">Consuntivo effettuato</SelectItem>
              <SelectItem value="no">Consuntivo NON effettuato</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.fatturato} onValueChange={v => setFilters({ ...filters, fatturato: v })}>
            <SelectTrigger className="w-[160px]" data-testid="filter-fatturato"><SelectValue placeholder="Fatturato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti (fatturaz.)</SelectItem>
              <SelectItem value="yes">Fatturato</SelectItem>
              <SelectItem value="no">Non fatturato</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 text-xs">
            <span className="text-slate-500">Apertura:</span>
            <Input type="date" value={filters.dataAperturaFrom} onChange={e => setFilters({ ...filters, dataAperturaFrom: e.target.value })} className="w-[140px]" data-testid="filter-data-from" />
            <span className="text-slate-400">→</span>
            <Input type="date" value={filters.dataAperturaTo} onChange={e => setFilters({ ...filters, dataAperturaTo: e.target.value })} className="w-[140px]" data-testid="filter-data-to" />
          </div>
          <div className="flex items-center gap-1 text-xs">
            <span className="text-slate-500">Scadenza:</span>
            <Input type="date" value={filters.scadenzaFrom} onChange={e => setFilters({ ...filters, scadenzaFrom: e.target.value })} className="w-[140px]" data-testid="filter-scad-from" />
            <span className="text-slate-400">→</span>
            <Input type="date" value={filters.scadenzaTo} onChange={e => setFilters({ ...filters, scadenzaTo: e.target.value })} className="w-[140px]" data-testid="filter-scad-to" />
          </div>
          <Button
            variant={filters.soloOrfani ? "default" : "outline"}
            size="sm"
            onClick={() => setFilters({ ...filters, soloOrfani: !filters.soloOrfani })}
            className={filters.soloOrfani ? "bg-amber-600 hover:bg-amber-700 text-white" : "border-amber-400 text-amber-800 hover:bg-amber-50"}
            data-testid="filter-orfani"
            title="Mostra solo ticket senza match anagrafica (da riassegnare)"
          >
            <AlertTriangle className="w-4 h-4 mr-1" />
            {filters.soloOrfani ? "Solo orfani ×" : `Orfani (${kpi.orfani})`}
          </Button>
          {kpi.orfani > 0 && canEdit("ticket") && (
            <Button
              variant="outline"
              size="sm"
              onClick={openMatchDialog}
              className="border-purple-400 text-purple-800 hover:bg-purple-50"
              data-testid="btn-match-orfani"
              title="Suggerisci automaticamente il cliente/stabilimento più simile per ogni orfano"
            >
              <Search className="w-4 h-4 mr-1" />
              Suggerisci match
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFilters({
              sede: "all", stato: "all", divisione: "all", tecnico: "all", urgente: "all", tipoGestione: "all",
              cliente: "all", stabilimento: "all", tipoRichiesta: "all", classifHwsw: "all",
              responsabile: "", consuntivo: "all", fatturato: "all",
              dataAperturaFrom: "", dataAperturaTo: "", scadenzaFrom: "", scadenzaTo: "",
              soloOrfani: false,
            })}
            data-testid="btn-reset-filters"
          >
            Reset filtri
          </Button>
        </div>
      </Card>

      <Card className="card-clean overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {canEdit("ticket") && (
                  <TableHead className="w-8">
                    <Checkbox
                      checked={isAllFilteredSel}
                      onCheckedChange={toggleAllFiltered}
                      data-testid="bulk-select-all"
                      aria-label="Seleziona tutti i ticket filtrati"
                    />
                  </TableHead>
                )}
                <TableHead className="w-[86px]">Numero</TableHead>
                <TableHead className="w-14 max-w-[76px] text-xs">E/G</TableHead>
                <TableHead className="max-w-[216px]">Cliente</TableHead>
                <TableHead className="hidden md:table-cell max-w-[144px]">Stabilimento</TableHead>
                <TableHead className="hidden md:table-cell w-[180px]">Oggetto sintetico</TableHead>
                <TableHead className="hidden lg:table-cell">Sede</TableHead>
                <TableHead className="w-12">Urg.</TableHead>
                <TableHead className="min-w-[228px]">Fasi</TableHead>
                <TableHead className="w-[86px]">Stato</TableHead>
                <TableHead className="hidden xl:table-cell w-[110px]">Aggiornato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(t => {
                const cli = data.clienti.find(c => c.id === t.cliente_id);
                const stab = data.stabilimenti.find(s => s.id === t.stabilimento_id);
                const isSel = selectedIds.has(t.id);
                return (
                  <TableRow key={t.id} className={`cursor-pointer hover:bg-blue-50/40 ${isSel ? "bg-purple-50" : ""}`} onClick={() => nav(`/ticket/${t.id}`)} data-testid={`ticket-row-${t.numero}`}>
                    {canEdit("ticket") && (
                      <TableCell onClick={(e) => e.stopPropagation()} className="w-8">
                        <Checkbox
                          checked={isSel}
                          onCheckedChange={() => toggleSel(t.id)}
                          data-testid={`bulk-select-${t.numero}`}
                          aria-label={`Seleziona ${t.numero}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-mono font-semibold text-blue-800 w-[86px]">{t.numero}{t.bozza && <span className="ml-1 text-xs text-amber-700">(bozza)</span>}</TableCell>
                    <TableCell className="font-mono text-[11px] w-14 max-w-[76px] truncate" title={t.codice_eg || ""}>{t.codice_eg || "—"}</TableCell>
                    <TableCell className="max-w-[216px] truncate">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate">{cli?.ragione_sociale || t.cliente_nome_storico || "—"}</span>
                        {!cli && t.cliente_nome_storico && canEdit("ticket") && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openReassign(t); }}
                            className="text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-2 py-0.5 hover:bg-amber-200 cursor-pointer flex items-center gap-1"
                            title="Riassegna a cliente/stabilimento dell'anagrafica"
                            data-testid={`btn-reassign-${t.numero}`}
                          >
                            storico · riassegna
                          </button>
                        )}
                        {stabPerCliente[cli?.id] > 1 && (
                          <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 border border-blue-200 rounded-full px-1.5 py-0.5" title={`${stabPerCliente[cli?.id]} stabilimenti`}>
                            +{stabPerCliente[cli?.id] - 1}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell max-w-[144px] truncate text-slate-600">{stab?.nome || t.stabilimento_nome_storico || ""}</TableCell>
                    <TableCell
                      className="hidden md:table-cell w-[180px] max-w-[180px] truncate text-slate-700 text-sm"
                      title={t.oggetto || ""}
                      data-testid={`ticket-oggetto-${t.numero}`}
                    >
                      {t.oggetto || <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-600">{t.sede}</TableCell>
                    <TableCell>{t.urgente ? <span className="urgente-flag" title="Urgente">● URG</span> : <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell>
                      <div className="fase-stepper">
                        {t.fasi?.map(f => (
                          <span key={f.num}
                            className={`fase-step ${f.stato === "Completata" ? "done" : f.stato === "In lavorazione" ? "wip" : f.stato === "Non applicabile" ? "na" : ""}`}
                            title={`Fase ${f.num} – ${FASI_NOMI[f.num-1]}: ${f.stato}`}>
                            {f.num}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="w-[86px]"><span className={`stato-pill ${statoClass(t.stato)}`}>{t.stato}</span></TableCell>
                    <TableCell className="hidden xl:table-cell w-[110px] text-xs text-slate-500">{fmtDate(t.updated_at)}</TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={canEdit("ticket") ? 11 : 10} className="text-center text-slate-500 py-10">Nessun ticket con questi filtri.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Barra azioni bulk (sticky bottom) */}
      {selectedIds.size > 0 && canEdit("ticket") && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-purple-900 text-white rounded-lg shadow-2xl border border-purple-700 flex items-center gap-2 px-4 py-3" data-testid="bulk-toolbar">
          <span className="text-sm font-semibold">{selectedIds.size} selezionat{selectedIds.size === 1 ? "o" : "i"}</span>
          <div className="w-px h-6 bg-purple-700"></div>
          <Button size="sm" variant="secondary" onClick={() => setBulkAction("stato")} data-testid="bulk-btn-stato">Cambia stato</Button>
          <Button size="sm" variant="secondary" onClick={() => setBulkAction("chiudi")} data-testid="bulk-btn-chiudi" className="bg-emerald-600 hover:bg-emerald-700 text-white">Chiudi</Button>
          {user?.role === "OWNER" && (
            <Button size="sm" variant="secondary" onClick={() => setBulkAction("delete")} data-testid="bulk-btn-delete" className="bg-red-600 hover:bg-red-700 text-white">Elimina</Button>
          )}
          <div className="w-px h-6 bg-purple-700"></div>
          <Button size="sm" variant="ghost" onClick={clearSel} data-testid="bulk-btn-clear" className="text-white hover:bg-purple-800">Deseleziona</Button>
        </div>
      )}

      {/* Dialog conferma bulk */}
      <Dialog open={!!bulkAction} onOpenChange={(o) => { if (!o) { setBulkAction(null); setBulkMotivazione(""); } }}>
        <DialogContent data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle>
              {bulkAction === "stato" && `Cambia stato di ${selectedIds.size} ticket`}
              {bulkAction === "chiudi" && `Chiudi ${selectedIds.size} ticket`}
              {bulkAction === "delete" && `Elimina ${selectedIds.size} ticket`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {bulkAction === "stato" && (
              <div>
                <Label>Nuovo stato</Label>
                <Select value={bulkStatoValue} onValueChange={setBulkStatoValue}>
                  <SelectTrigger data-testid="bulk-stato-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OnGoing">OnGoing</SelectItem>
                    <SelectItem value="In attesa cliente">In attesa cliente</SelectItem>
                    <SelectItem value="Sospeso">Sospeso</SelectItem>
                    <SelectItem value="Riaperto">Riaperto</SelectItem>
                    <SelectItem value="Annullato">Annullato</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-slate-500 mt-2">Nota: per "Chiuso" usa il pulsante "Chiudi" così viene registrato lo storico chiusura.</div>
              </div>
            )}
            {bulkAction === "chiudi" && (
              <div>
                <Label>Motivazione (facoltativa)</Label>
                <Textarea
                  rows={3}
                  value={bulkMotivazione}
                  onChange={e => setBulkMotivazione(e.target.value)}
                  placeholder="Es. Chiusura in blocco fine anno / risoluzione bulk / …"
                  data-testid="bulk-motivazione"
                />
                <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-2 mt-2">
                  I ticket passano a stato "Chiuso" con storico chiusura registrato. La fase 8 viene marcata come completata.
                </div>
              </div>
            )}
            {bulkAction === "delete" && (
              <div className="text-sm bg-red-50 border border-red-300 text-red-900 rounded p-3">
                <strong>Attenzione:</strong> l'eliminazione è definitiva. Verranno rimossi anche gli interventi, rapporti, consuntivi, conferme, fatture, documenti e aggiornamenti collegati a questi {selectedIds.size} ticket.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkAction(null); setBulkMotivazione(""); }} disabled={bulkBusy}>Annulla</Button>
            <Button
              onClick={runBulk}
              disabled={bulkBusy}
              data-testid="bulk-confirm"
              className={bulkAction === "delete" ? "bg-red-700 hover:bg-red-800 text-white" : bulkAction === "chiudi" ? "bg-emerald-700 hover:bg-emerald-800 text-white" : ""}
            >
              {bulkBusy ? "In corso…" : bulkAction === "delete" ? "Elimina definitivamente" : bulkAction === "chiudi" ? "Chiudi in blocco" : "Applica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nuovo ticket dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-2xl" data-testid="nuovo-ticket-dialog">
          <DialogHeader><DialogTitle>Nuovo ticket · Fase 1 – Apertura</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Numero ticket *</Label>
              <Input
                value={form.numero}
                onChange={e => setForm({ ...form, numero: e.target.value })}
                placeholder="Es. P26-0001, TKT-2026-01, o qualsiasi codice interno"
                data-testid="new-numero"
                className={numeroDuplicato ? "border-red-500 focus-visible:ring-red-500" : ""}
              />
              {numeroDuplicato ? (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-1 flex items-center gap-1" data-testid="numero-duplicate-warning">
                  <AlertTriangle className="w-3 h-3" /> Numero <span className="font-mono font-semibold">{numeroDuplicato}</span> già esistente — scegline un altro.
                </div>
              ) : (
                <div className="text-xs text-slate-500 mt-1">Testo libero — puoi seguire il tuo standard interno.</div>
              )}
            </div>
            <div>
              <Label>Data richiesta *</Label>
              <Input
                type="date"
                value={form.data_richiesta}
                onChange={e => setForm({ ...form, data_richiesta: e.target.value })}
                data-testid="new-data"
              />
              <div className="text-xs text-slate-500 mt-1">Giorno di apertura/segnalazione. Default: oggi.</div>
            </div>
            <div>
              <Label>Cliente *</Label>
              <Combobox
                value={form.cliente_id}
                onChange={v => setForm({ ...form, cliente_id: v, stabilimento_id: "" })}
                options={data.clienti.filter(c => c.attivo).map(c => ({ value: c.id, label: c.ragione_sociale }))}
                placeholder="Seleziona cliente…"
                searchPlaceholder="Cerca cliente…"
                emptyText="Nessun cliente trovato"
                data-testid="new-cliente"
              />
            </div>
            <div>
              <Label>Stabilimento *</Label>
              <Combobox
                value={form.stabilimento_id}
                onChange={v => setForm({ ...form, stabilimento_id: v })}
                options={stabilimentiFiltrati.map(s => ({ value: s.id, label: s.nome }))}
                placeholder={form.cliente_id ? "Seleziona stabilimento…" : "Prima scegli il cliente"}
                searchPlaceholder="Cerca stabilimento…"
                emptyText="Nessuno stabilimento trovato"
                disabled={!form.cliente_id}
                data-testid="new-stabilimento"
              />
            </div>
            <div>
              <Label>Contatto / Richiedente</Label>
              <Input
                value={form.contatto_nome}
                onChange={e => setForm({ ...form, contatto_nome: e.target.value })}
                placeholder="Es. Mario Rossi · ufficio tecnico"
                data-testid="new-contatto"
              />
              <div className="text-xs text-slate-500 mt-1">Testo libero — potrà poi essere aggiunto all'anagrafica contatti.</div>
            </div>
            <div>
              <Label>Email contatto</Label>
              <Input
                type="email"
                value={form.contatto_email}
                onChange={e => setForm({ ...form, contatto_email: e.target.value })}
                placeholder="mario.rossi@azienda.it"
                data-testid="new-contatto-email"
              />
              <div className="text-xs text-slate-500 mt-1">Facoltativa.</div>
            </div>
            <div>
              <Label>Telefono contatto</Label>
              <Input
                value={form.contatto_telefono}
                onChange={e => setForm({ ...form, contatto_telefono: e.target.value })}
                placeholder="+39 ..."
                data-testid="new-contatto-telefono"
              />
              <div className="text-xs text-slate-500 mt-1">Facoltativo.</div>
            </div>
            <div>
              <Label>Canale</Label>
              <Select value={form.canale} onValueChange={v => setForm({ ...form, canale: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="telefono">Telefono</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="commerciale">Commerciale</SelectItem>
                  <SelectItem value="tecnico_in_cantiere">Tecnico in cantiere</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Natura</Label>
              <Select value={form.natura} onValueChange={v => setForm({ ...form, natura: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="SW">SW</SelectItem><SelectItem value="HW">HW</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sede Emmepi responsabile</Label>
              <Select value={form.sede} onValueChange={v => setForm({ ...form, sede: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Perugia">Perugia</SelectItem><SelectItem value="Treviso">Treviso</SelectItem><SelectItem value="Milano">Milano</SelectItem><SelectItem value="Novara">Novara</SelectItem><SelectItem value="Pieri">Pieri</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 mt-6">
              <Checkbox id="urg" checked={form.urgente} onCheckedChange={v => setForm({ ...form, urgente: !!v })} data-testid="new-urgente" />
              <Label htmlFor="urg" className="cursor-pointer">Urgente</Label>
            </div>
            <div className="md:col-span-2">
              <Label>Oggetto sintetico *</Label>
              <Textarea rows={2} value={form.oggetto} onChange={e => setForm({ ...form, oggetto: e.target.value })} data-testid="new-oggetto" placeholder="Es. Guasto avvolgitore linea 3 · sostituzione pignone" />
              <div className="text-xs text-slate-500 mt-1">La descrizione estesa potrà essere compilata nella Fase 1 Apertura · fino a quel momento la fase resterà in giallo (In lavorazione).</div>
            </div>
            <div className="md:col-span-2">
              <Label>Note interne</Label>
              <Textarea rows={2} value={form.note_interne} onChange={e => setForm({ ...form, note_interne: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)}>Annulla</Button>
            <Button onClick={createTicket} data-testid="new-save" disabled={!!numeroDuplicato}>Salva ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Match automatico orfani */}
      <Dialog open={openMatch} onOpenChange={setOpenMatch}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="match-orfani-dialog">
          <DialogHeader>
            <DialogTitle>Match automatico ticket orfani</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-4 bg-purple-50 border border-purple-200 rounded p-3">
              <div className="text-sm text-purple-900 flex-shrink-0">
                <div className="font-semibold">Soglia similarità: {Math.round(matchSoglia * 100)}%</div>
                <div className="text-xs opacity-80">Più bassa = più candidati (anche imprecisi); più alta = solo match sicuri.</div>
              </div>
              <input
                type="range"
                min="0.2"
                max="0.95"
                step="0.05"
                value={matchSoglia}
                onChange={e => setMatchSoglia(parseFloat(e.target.value))}
                onMouseUp={() => loadMatchSuggestions()}
                onTouchEnd={() => loadMatchSuggestions()}
                className="flex-1"
                data-testid="match-soglia-slider"
              />
              <Button size="sm" variant="outline" onClick={() => loadMatchSuggestions()} disabled={matchLoading} data-testid="btn-match-reload">
                {matchLoading ? "Calcolo…" : "Ricalcola"}
              </Button>
            </div>
            <div className="text-xs text-slate-600">
              Preselezionati (spunta ✓) solo i match con confidenza cliente ≥ 70%. Puoi cambiare cliente/stabilimento per ogni riga o spuntare manualmente quelli a bassa confidenza.
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Ticket</TableHead>
                    <TableHead>Storico (cliente / stabilimento)</TableHead>
                    <TableHead>Cliente suggerito</TableHead>
                    <TableHead>Stabilimento</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matchLoading && (
                    <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500">Calcolo dei match in corso…</TableCell></TableRow>
                  )}
                  {!matchLoading && matchResults.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500">Nessun ticket orfano — tutti già riassegnati!</TableCell></TableRow>
                  )}
                  {!matchLoading && matchResults.map(row => {
                    const sel = matchSelection[row.ticket_id] || { cliente_id: "", stabilimento_id: "", enabled: false };
                    const selectedCand = row.candidati?.find(c => c.cliente_id === sel.cliente_id);
                    const noCandidates = !row.candidati?.length;
                    return (
                      <TableRow key={row.ticket_id} data-testid={`match-row-${row.numero}`} className={sel.enabled ? "bg-purple-50/40" : ""}>
                        <TableCell>
                          <Checkbox
                            checked={sel.enabled}
                            onCheckedChange={v => setMatchSelection({ ...matchSelection, [row.ticket_id]: { ...sel, enabled: !!v } })}
                            disabled={noCandidates || !sel.cliente_id}
                            data-testid={`match-check-${row.numero}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{row.numero}</TableCell>
                        <TableCell className="text-xs">
                          <div className="font-medium text-amber-900">{row.cliente_nome_storico || "—"}</div>
                          <div className="text-slate-500">{row.stabilimento_nome_storico || "—"}</div>
                        </TableCell>
                        <TableCell className="min-w-[220px]">
                          {noCandidates ? (
                            <span className="text-xs text-red-600 italic">Nessun match sopra soglia</span>
                          ) : (
                            <select
                              className="w-full text-sm border border-slate-300 rounded px-2 py-1"
                              value={sel.cliente_id}
                              onChange={e => {
                                const cand = row.candidati.find(c => c.cliente_id === e.target.value);
                                setMatchSelection({
                                  ...matchSelection,
                                  [row.ticket_id]: {
                                    cliente_id: e.target.value,
                                    stabilimento_id: cand?.best_stabilimento?.id || "",
                                    enabled: sel.enabled,
                                  }
                                });
                              }}
                              data-testid={`match-cli-${row.numero}`}
                            >
                              <option value="">— nessuno —</option>
                              {row.candidati.map(c => {
                                const cli = Math.round((c.score_cliente || 0) * 100);
                                const stb = Math.round((c.score_stab_best || 0) * 100);
                                const comb = Math.round((c.score_combinato || c.score_cliente || 0) * 100);
                                const hint = stb > cli ? ` (via stab ${stb}%)` : "";
                                return (
                                  <option key={c.cliente_id} value={c.cliente_id}>
                                    {comb}% · {c.cliente_nome}{hint}
                                  </option>
                                );
                              })}
                            </select>
                          )}
                        </TableCell>
                        <TableCell className="min-w-[220px]">
                          {selectedCand?.stabilimenti?.length ? (
                            <select
                              className="w-full text-sm border border-slate-300 rounded px-2 py-1"
                              value={sel.stabilimento_id || ""}
                              onChange={e => setMatchSelection({
                                ...matchSelection,
                                [row.ticket_id]: { ...sel, stabilimento_id: e.target.value }
                              })}
                              data-testid={`match-stab-${row.numero}`}
                            >
                              <option value="">— non collegare —</option>
                              {selectedCand.stabilimenti.map(s => (
                                <option key={s.id} value={s.id}>
                                  {s.score > 0 ? `${Math.round(s.score * 100)}% · ` : ""}{s.codice_completo ? s.codice_completo + " " : ""}{s.nome}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-xs text-slate-400 italic">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {!matchLoading && matchResults.length > 0 && (
              <div className="text-xs text-slate-600 flex items-center gap-4">
                <span>Selezionati: <span className="font-semibold text-purple-800">{Object.values(matchSelection).filter(v => v.enabled).length}</span> / {matchResults.length}</span>
                <Button size="sm" variant="ghost" onClick={() => {
                  const s = {};
                  matchResults.forEach(r => {
                    const cur = matchSelection[r.ticket_id];
                    s[r.ticket_id] = { ...(cur || {}), enabled: !!(cur?.cliente_id) };
                  });
                  setMatchSelection(s);
                }} data-testid="btn-match-select-all">Seleziona tutti con match</Button>
                <Button size="sm" variant="ghost" onClick={() => {
                  const s = {};
                  matchResults.forEach(r => { s[r.ticket_id] = { ...(matchSelection[r.ticket_id] || {}), enabled: false }; });
                  setMatchSelection(s);
                }} data-testid="btn-match-deselect-all">Deseleziona tutti</Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenMatch(false)} disabled={matchApplying}>Chiudi</Button>
            <Button
              onClick={applyMatches}
              disabled={matchApplying || matchLoading || !Object.values(matchSelection).some(v => v.enabled && v.cliente_id)}
              className="bg-purple-700 hover:bg-purple-800 text-white"
              data-testid="btn-match-apply"
            >
              {matchApplying ? "Applicazione…" : `Applica ${Object.values(matchSelection).filter(v => v.enabled).length} riassegnazioni`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Riassegna anagrafica per ticket orfani */}
      <Dialog open={!!reassignTicket} onOpenChange={(o) => { if (!o) setReassignTicket(null); }}>
        <DialogContent className="max-w-lg" data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle>Riassegna anagrafica · Ticket {reassignTicket?.numero}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-2">
              <div><span className="font-semibold">Cliente storico:</span> {reassignTicket?.cliente_nome_storico || "—"}</div>
              <div><span className="font-semibold">Stabilimento storico:</span> {reassignTicket?.stabilimento_nome_storico || "—"}</div>
              <div className="mt-1 text-[11px] opacity-80">Selezionando cliente/stabilimento dall'anagrafica, i nomi storici verranno rimossi e il ticket sarà collegato al record ufficiale.</div>
            </div>
            <div>
              <Label>Cliente (anagrafica) *</Label>
              <Combobox
                value={reassignForm.cliente_id}
                onChange={v => setReassignForm({ cliente_id: v, stabilimento_id: "", contatto_id: "" })}
                options={data.clienti.filter(c => c.attivo).map(c => ({ value: c.id, label: c.ragione_sociale }))}
                placeholder="Cerca cliente…"
                searchPlaceholder="Cerca cliente…"
                emptyText="Nessun cliente trovato"
                data-testid="reassign-cliente"
              />
            </div>
            <div>
              <Label>Stabilimento *</Label>
              <Combobox
                value={reassignForm.stabilimento_id}
                onChange={v => setReassignForm({ ...reassignForm, stabilimento_id: v })}
                options={reassignStabOptions}
                placeholder={reassignForm.cliente_id ? "Seleziona stabilimento…" : "Prima scegli il cliente"}
                searchPlaceholder="Cerca stabilimento…"
                emptyText="Nessuno stabilimento per questo cliente"
                disabled={!reassignForm.cliente_id}
                data-testid="reassign-stabilimento"
              />
            </div>
            <div>
              <Label>Contatto (opzionale)</Label>
              <Combobox
                value={reassignForm.contatto_id}
                onChange={v => setReassignForm({ ...reassignForm, contatto_id: v })}
                options={reassignContattoOptions}
                placeholder="Nessuno"
                searchPlaceholder="Cerca contatto…"
                emptyText="Nessun contatto disponibile"
                data-testid="reassign-contatto"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignTicket(null)} disabled={reassignBusy}>Annulla</Button>
            <Button onClick={saveReassign} disabled={reassignBusy || !reassignForm.cliente_id || !reassignForm.stabilimento_id} data-testid="btn-reassign-save">
              {reassignBusy ? "Salvataggio…" : "Riassegna"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Simula email */}
      <Dialog open={openEmail} onOpenChange={setOpenEmail}>
        <DialogContent data-testid="email-sim-dialog">
          <DialogHeader><DialogTitle>Simulazione richiesta da email</DialogTitle></DialogHeader>
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0" /> Non vengono lette caselle reali. Verrà creata una bozza da completare.</div>
          <div className="space-y-2 mt-2">
            <div><span className="text-slate-500 text-xs">Da:</span> <span className="font-mono">{EMAIL_SIM.from}</span></div>
            <div><span className="text-slate-500 text-xs">Oggetto:</span> <span className="font-medium">{EMAIL_SIM.subject}</span></div>
            <pre className="bg-slate-50 p-3 rounded text-sm text-slate-700 whitespace-pre-wrap font-sans">{EMAIL_SIM.body}</pre>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEmail(false)}>Annulla</Button>
            <Button onClick={createBozzaFromEmail} data-testid="email-sim-create">Crea bozza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
